import PropTypes from "prop-types"
import { Paper } from "@mui/material"
import { useSyncGraphPreferences } from "connected-components/dashboard/hooks/use-sync-graph-preferences"
import { useGraphData } from "./hooks/use-graph-data"
import performanceTemplate from "./templates/performance"
import riskTemplate from "./templates/risk"
import exposureTemplate from "./templates/exposure"
import contentSetTemplate from "./templates/content-set"
import { historical as factorSensitivityHistorical, predicted as factorSensitivityPredicted } from "./templates/factor-sensitivity"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { INSTRUMENTS_WITH_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { CONTENT_SET_TEMPLATE_PREFIX } from "./components/template-selector"

const NAMED_TEMPLATES = {
  performance: performanceTemplate,
  risk: riskTemplate,
  exposure: exposureTemplate,
  "factor-sensitivity-historical": factorSensitivityHistorical,
  "factor-sensitivity-predicted": factorSensitivityPredicted,
}

function resolveTemplate(selectedTemplate) {
  if (selectedTemplate?.startsWith(CONTENT_SET_TEMPLATE_PREFIX)) return contentSetTemplate
  return NAMED_TEMPLATES[selectedTemplate] || NAMED_TEMPLATES.performance
}

export default function GraphWrapper(props) {
  const { initialSelectedTemplate, initialDateOverride, instrumentsWithMetadata, modelMetadata } = props
  // useGraphData seeds the module-config template; call it before useSyncGraphPreferences so the
  // seed effect registers (and runs) first, becoming the baseline a saved preference then overrides.
  const { selectedTemplate, isGraphInitialized } = useGraphData(initialSelectedTemplate, initialDateOverride)
  // Sync once per panel here — not inside useGraphData, which every template + the selector also
  // call (that would fire N redundant syncs and crash outside a panel with no PreferenceContext).
  useSyncGraphPreferences()

  // When a module-config template is provided, hold the template tree until the seed has populated
  // the slice. Otherwise the nested selector/templates (which read the slice with no arg) would
  // briefly resolve DEFAULT while this wrapper resolves the configured template — a wrong-graph
  // flash and, for content-set panels, a frame with no active content set.
  if (initialSelectedTemplate != null && !isGraphInitialized) {
    return <div className="graph-wrapper" />
  }

  const template = resolveTemplate(selectedTemplate)

  // sensitivityType is defined only on the factor-sensitivity templates; harmless elsewhere.
  return (
    <div className="graph-wrapper">
      <Paper square className="graph-header" elevation={0}>
        <template.Header modelMetadata={modelMetadata} sensitivityType={template.sensitivityType} />
      </Paper>
      <template.Component
        instrumentsWithMetadata={instrumentsWithMetadata}
        modelMetadata={modelMetadata}
        sensitivityType={template.sensitivityType}
      />
    </div>
  )
}

GraphWrapper.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  instrumentsWithMetadata: INSTRUMENTS_WITH_METADATA_SHAPE.isRequired,
  initialSelectedTemplate: PropTypes.string,
  initialDateOverride: PropTypes.shape({
    period: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
}
